import React, {useEffect, useState} from 'react';
import {
  generateProcedureCPTCode,
  getAllProcedures,
  getCPTCodesResult
} from '@shared/services/SmartCoderServices';
import {useForm} from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import './GenerateCPTCodeDailog.scss';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {FileCopy} from '@material-ui/icons';
import Loader from '@components/framework/loader/Loader';
import GenerateProcedureCPTCodeTab from './GenerateProcedureCodeTab/GenerateProcedureCPTCodeTab';
import InvoicesStyledTabs from '@components/system/Tabs/InvoicesStyledTabs';
import InvoicesTab from '@components/system/Tabs/InvoicesTab';
import GenerateSedationCPTCodeTab from './GenerateSedationCPTCodeTab/GenerateSedationCPTCodeTab';
import GenerateHcpcsCodeTab from './GenerateHcpcsCodeTab/GenerateHcpcsCodeTab';
import {
  GENERATE_CPT_CODE_TAB_ID,
  SMART_CODER_PROCEDURE_TYPES
} from '@shared/constants/Mapping';
import {FormField} from '@components/system/FormField/FormField';
import FindProcessingTimes from './ProcessingTimesTab/ProcessingTimes';
import {IProcedure} from '@shared/services/service-mappers/SmartCoderServiceMapper';

export interface IGenerateCPTCodeDialogProps {
  clinicGroupId: number;
  clinicId: number;
  open: boolean;
  onClose: (res: 'confirm' | 'cancel') => void;
  confirmButtonText?: string;
  onSubmit?: (values: {
    cptCode: string | null;
    doctorsNotes: string;
  }) => void | Promise<void>;
  closeButtonText?: string;
  relatedType: string; // It should one of the value defined in SMART_CODER_RELATED_TYPES variable in mappings
  relatedId?: number;
  showConfirmButton?: boolean;
  dialogHelperText?: string;
  showPriorAuthInfo?: boolean;
  priorAuthTicketDetails?: any;
  visitType?: string | null;
  doctorsNotes?: string;
}

const useStyles = makeStyles(() => {
  return {
    tab: {
      border: `1px solid #c1c1c1`,
      height: '44px',
      flexShrink: 0,
      '& button': {
        fontWeight: 600
      },
      marginBottom: '1rem'
    }
  };
});

const GenerateCPTCodeDialog = (
  props: IGenerateCPTCodeDialogProps
): JSX.Element => {
  const {t} = useTranslation();
  const classes = useStyles();
  const {
    clinicGroupId,
    clinicId,
    open,
    onClose,
    confirmButtonText = t('COMMON.CONFIRM'),
    onSubmit,
    closeButtonText = t('COMMON.CANCEL'),
    relatedId,
    relatedType,
    showConfirmButton = true,
    dialogHelperText = t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.DESCRIPTION'),
    showPriorAuthInfo = false,
    priorAuthTicketDetails = null,
    visitType = null,
    doctorsNotes = ''
  } = props;

  const defaultDoctorsNote = doctorsNotes;
  const currentCPTCode = priorAuthTicketDetails?.procedure_code ?? null;

  const useFormMethods = useForm<any>({
    defaultValues: {
      doctors_notes: defaultDoctorsNote
    },
    shouldUnregister: false
  });

  const {handleSubmit} = useFormMethods;

  const [generatedCPTCode, setGeneratedCPTCode] = useState({
    cptCode: null,
    isLoading: false,
    procedureId: null
  });

  const clearGeneratedCPTCode = () => {
    setGeneratedCPTCode({
      cptCode: null,
      isLoading: false,
      procedureId: null
    });
  };

  const [previousCPTCodeResult, setPreviousCPTCodeResult] = useState({
    data: null,
    isLoading: false
  });

  // This is to store the form error/backend error received on form submission.
  const [formError, setFormError] = useState(null);

  const clearFormError = () => {
    setFormError(null);
  };

  useEffect(() => {
    const getPreviousCPTResult = async () => {
      try {
        setPreviousCPTCodeResult(p => ({
          ...p,
          isLoading: true
        }));
        const res = await getCPTCodesResult({
          clinicGroupId,
          clinicId,
          params: {
            related_id: relatedId,
            related_type: relatedType
          }
        });
        if (res?.data?.values && currentCPTCode === res?.data?.cpt_code) {
          setPreviousCPTCodeResult(p => ({
            data: res.data.values,
            isLoading: false
          }));
          setGeneratedCPTCode({
            cptCode: res?.data?.cpt_code ?? null,
            isLoading: false,
            procedureId: res?.data?.procedure_code_id ?? null
          });
        } else {
          setPreviousCPTCodeResult(p => ({
            data: null,
            isLoading: false
          }));
        }
      } catch (e) {
        setPreviousCPTCodeResult(p => ({
          data: null,
          isLoading: false
        }));
      }
    };
    if (relatedId && relatedType) {
      getPreviousCPTResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicGroupId, clinicId, relatedId, relatedType]);

  async function copyTextToClipboard(text: string) {
    if ('clipboard' in navigator) {
      return await navigator.clipboard.writeText(text);
    } else {
      return document.execCommand('copy', true, text);
    }
  }

  const handleCopyClick = async () => {
    await copyTextToClipboard(generatedCPTCode.cptCode);
    enqueueSnackbar(t('COMMON.COPY_SUCCESS'), SnackBarConfig('i'));
  };

  const handleClose = (res: 'confirm' | 'cancel' = 'cancel') => {
    onClose('cancel');
  };
  const {enqueueSnackbar} = useSnackbar();

  const [procedures, setProcedures] = useState({
    isLoading: false,
    data: [] as IProcedure[]
  });

  useEffect(() => {
    const fetchProcedures = async () => {
      setProcedures(p => ({
        ...p,
        isLoading: true
      }));
      try {
        const procedureRes = await getAllProcedures({
          clinicGroupId,
          clinicId,
          params: {
            type: SMART_CODER_PROCEDURE_TYPES.PAIN_MED_PROCEDURE
          }
        });
        if (Array.isArray(procedureRes.data)) {
          setProcedures({
            data: procedureRes.data,
            isLoading: false
          });
        } else {
          setProcedures({
            data: [],
            isLoading: false
          });
        }
      } catch (err) {
        setProcedures({
          data: [],
          isLoading: false
        });
      }
    };
    fetchProcedures();
  }, [clinicGroupId, clinicId]);

  const handleGenerateCPTCode = async values => {
    clearFormError();
    try {
      setGeneratedCPTCode({
        cptCode: null,
        isLoading: true,
        procedureId: null
      });
      const res = await generateProcedureCPTCode({
        clinicGroupId,
        clinicId,
        payload: {
          ...values,
          related_type: relatedType
        }
      });
      if (res.data) {
        setGeneratedCPTCode({
          cptCode: res.data.cpt_code,
          isLoading: false,
          procedureId: values?.procedure_code_id
        });
      }
      return res?.data?.cpt_code;
    } catch (e) {
      if (e?.message) {
        enqueueSnackbar(e.message, SnackBarConfig('e'));
        setFormError(e.message);
      }
      setGeneratedCPTCode({
        cptCode: null,
        isLoading: false,
        procedureId: null
      });
    }
  };

  const handleFormSubmit = async values => {
    clearFormError();
    values.related_id = relatedId;
    await handleGenerateCPTCode(values);
    if (onSubmit) {
      await onSubmit({
        cptCode: generatedCPTCode.cptCode,
        doctorsNotes: values.doctors_notes
      });
    }
    onClose('confirm');
  };

  const renderCPTCode = () => {
    if (generatedCPTCode.isLoading) {
      return <Loader size={18} />;
    }
    if (!generatedCPTCode.cptCode) {
      return t(
        'SMART_CODER.GENERATE_CPT_CODES_DIALOG.PROVIDE_FIELDS_TO_GENERATE_CPTS_HELPER_TEXT'
      );
    }
    const procedure = procedures.data.find(
      p => p.id == generatedCPTCode.procedureId
    );
    return (
      <>
        <div className="cpt-code-text">
          <span className="cpt-code-title">
            {procedure?.label ? `${procedure.label} And ` : ''}
            {t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.CPT_CODES')}:
          </span>{' '}
          {generatedCPTCode.cptCode}
        </div>
        <div>
          <Button
            onClick={handleCopyClick}
            variant="text"
            color="primary"
            disabled={generatedCPTCode.isLoading || !generatedCPTCode.cptCode}
            startIcon={<FileCopy />}>
            {t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.COPY_CPT_CODE')}
          </Button>
        </div>
      </>
    );
  };

  const handleFormError = (formErrors: Record<string, {message: string}>) => {
    const keys = Object.keys(formErrors ?? {});
    if (keys.length > 0) {
      if (formErrors?.[keys[0]]?.message) {
        setFormError(formErrors[keys[0]].message);
      } else {
        const nestedKeys = Object.keys(formErrors[keys[0]] ?? {});
        if (
          nestedKeys.length > 0 &&
          formErrors?.[keys[0]]?.[nestedKeys[0]]?.message
        ) {
          setFormError(formErrors[keys[0]][nestedKeys[0]].message);
        }
      }
    }
  };

  const [submitButtonState, setSubmitButtonState] = useState({
    isDisabled: false,
    isLoading: false
  });

  const updateSubmitButtonState = ({
    isDisabled = false,
    isLoading = false
  }: {
    isDisabled: boolean;
    isLoading: boolean;
  }) => {
    setSubmitButtonState({
      isDisabled: isDisabled,
      isLoading
    });
  };

  const disableSubmitButton =
    generatedCPTCode.isLoading ||
    !generatedCPTCode.cptCode ||
    submitButtonState.isDisabled;

  const generateCptCodeTabs = [
    {
      id: GENERATE_CPT_CODE_TAB_ID.PROCEDURE,
      label: t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.PROCEDURE_TAB_LABEL')
    },
    {
      id: GENERATE_CPT_CODE_TAB_ID['SEDATION/ANESTHESIA'],
      label: t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.SEDATION_TAB_LABEL')
    },
    {
      id: GENERATE_CPT_CODE_TAB_ID['HCPCS'],
      label: t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.HCPCS_TAB_LABEL')
    },
    {
      id: GENERATE_CPT_CODE_TAB_ID['PROCESSING_TIMES'],
      label: t(
        'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.PROCESSING_TIMES_TAB_LABEL'
      )
    }
  ];

  const [activeTabId, setActiveTabId] = useState(
    GENERATE_CPT_CODE_TAB_ID.PROCEDURE
  );
  const handleTabChange = (event, newValue: string) => {
    setActiveTabId(newValue);
  };

  const handleChangeTab = (newValue: string) => {
    setActiveTabId(newValue);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen
      className="generate-cpt-code-dialog-wrapper">
      <DialogTitle
        disableTypography
        className={'generate-cpt-code-dialog-title'}>
        {t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.TITLE')}
      </DialogTitle>
      <DialogContent
        dividers
        className="generate-cpt-code-content-wrapper"
        key={previousCPTCodeResult.isLoading.toString()}>
        <div className="dialog-helper-text">{dialogHelperText}</div>
        <InvoicesStyledTabs
          value={activeTabId}
          variant="scrollable"
          scrollButtons={'auto'}
          className={classes.tab}
          onChange={handleTabChange}>
          {generateCptCodeTabs.map((tab: any) => {
            return (
              <InvoicesTab value={tab.id} label={tab.label} key={tab.label} />
            );
          })}
        </InvoicesStyledTabs>
        <div className="tab-wrapper">
          {previousCPTCodeResult.isLoading && <Loader />}
          {!previousCPTCodeResult.isLoading && (
            <>
              <div
                className={`tab ${
                  activeTabId === GENERATE_CPT_CODE_TAB_ID.PROCEDURE
                    ? ''
                    : 'hide-tab'
                }`}>
                <GenerateProcedureCPTCodeTab
                  clinicGroupId={clinicGroupId}
                  clinicId={clinicId}
                  relatedId={relatedId}
                  relatedType={relatedType}
                  updateSubmitButtonState={updateSubmitButtonState}
                  useFormMethods={useFormMethods}
                  clearGeneratedCPTCode={clearGeneratedCPTCode}
                  defaultValues={previousCPTCodeResult?.data}
                />
              </div>
              <div
                className={`tab ${
                  activeTabId ===
                  GENERATE_CPT_CODE_TAB_ID['SEDATION/ANESTHESIA']
                    ? ''
                    : 'hide-tab'
                }`}>
                <GenerateSedationCPTCodeTab
                  clinicGroupId={clinicGroupId}
                  clinicId={clinicId}
                  relatedId={relatedId}
                  relatedType={relatedType}
                  updateSubmitButtonState={updateSubmitButtonState}
                  useFormMethods={useFormMethods}
                  clearGeneratedCPTCode={clearGeneratedCPTCode}
                  defaultValues={previousCPTCodeResult?.data}
                  onTabChange={handleChangeTab}
                />
              </div>
              <div
                className={`tab ${
                  activeTabId === GENERATE_CPT_CODE_TAB_ID['HCPCS']
                    ? ''
                    : 'hide-tab'
                }`}>
                <GenerateHcpcsCodeTab
                  clinicGroupId={clinicGroupId}
                  clinicId={clinicId}
                  useFormMethods={useFormMethods}
                  defaultValues={previousCPTCodeResult?.data}
                  relatedId={relatedId}
                  relatedType={relatedType}
                  clearGeneratedCPTCode={clearGeneratedCPTCode}
                  onTabChange={handleChangeTab}
                />
              </div>
              <div
                className={`tab ${
                  activeTabId === GENERATE_CPT_CODE_TAB_ID['PROCESSING_TIMES']
                    ? ''
                    : 'hide-tab'
                }`}>
                <FindProcessingTimes
                  clinicGroupId={clinicGroupId}
                  clinicId={clinicId}
                  useFormMethods={useFormMethods}
                  handleChangeTabToProcedure={handleChangeTab}
                />
              </div>
              <div
                className="tab-right"
                style={{
                  display:
                    activeTabId === GENERATE_CPT_CODE_TAB_ID['PROCESSING_TIMES']
                      ? 'none'
                      : 'flex'
                }}>
                {showPriorAuthInfo && (
                  <>
                    <div className="visit_type">
                      <span className="visit_type_label">
                        {t(
                          'SMART_CODER.GENERATE_CPT_CODES_DIALOG.VISIT_TYPE_LABEL'
                        )}
                        :{' '}
                      </span>
                      {visitType ? visitType : '-'}
                    </div>
                    <FormField
                      readonly
                      disabled
                      value={priorAuthTicketDetails?.notes}
                      label={t(
                        'SMART_CODER.GENERATE_CPT_CODES_DIALOG.PRIOR_AUTH_NOTES'
                      )}
                      type={'textarea'}
                      placeholder={''}
                      className="prior-auth-notes-field"
                      required={false}
                    />
                  </>
                )}
                {/* {activeTabId !==
                  GENERATE_CPT_CODE_TAB_ID['PROCESSING_TIMES'] && (
                  <Controller
                    control={useFormMethods.control}
                    name={'doctors_notes'}
                    render={inputProps => {
                      return (
                        <FormField
                          label={t(
                            'SMART_CODER.GENERATE_CPT_CODES_DIALOG.DOCTORS_NOTES'
                          )}
                          type={'textarea'}
                          placeholder={t(
                            'SMART_CODER.GENERATE_CPT_CODES_DIALOG.DOCTORS_NOTES_PLACEHOLDER'
                          )}
                          formControlClass={'doctors-notes-field-form-control'}
                          className={'doctors-notes-field'}
                          required={false}
                          {...inputProps}
                          error={useFormMethods.errors['doctors_notes']}
                        />
                      );
                    }}
                  />
                )} */}
              </div>
            </>
          )}
        </div>
        <div className="generated-cpt-code-wrapper">
          <div className="cpt-code-wrapper">{renderCPTCode()}</div>
          <Button
            onClick={handleSubmit(handleGenerateCPTCode, handleFormError)}
            variant="outlined"
            color="primary"
            size="large">
            {t('SMART_CODER.GENERATE_CPT_CODES_DIALOG.GENERATE_CPT_CODES')}
          </Button>
        </div>
        {formError && <div className="error-wrapper">{formError}</div>}
      </DialogContent>
      <DialogActions>
        <Button
          color={showConfirmButton ? 'default' : 'primary'}
          variant={showConfirmButton ? 'text' : 'contained'}
          onClick={() => handleClose('cancel')}>
          {closeButtonText}
        </Button>
        {showConfirmButton && (
          <Button
            disabled={disableSubmitButton}
            color="primary"
            variant="contained"
            onClick={handleSubmit(handleFormSubmit, handleFormError)}>
            {confirmButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateCPTCodeDialog;
